import { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import { collection, limit, onSnapshot, orderBy, query } from 'firebase/firestore';
import { buildAppsationsFunctionUrl } from '@/lib/appsationsSdkEndpoints';
import { spaceWeatherWaveformRingConfigs, type SpaceWeatherReport } from './spaceWeatherWaveformConfig';

type RingRow = {
  timestamp: string;
  value: string;
  numericValue?: number;
  stageLabel?: string;
  productId?: string;
  message?: string;
};

const getDb = async () => {
  const { db } = await import('@/lib/firebase');
  return db;
};

const normalizeWaveformReports = (payload: unknown): SpaceWeatherReport[] => {
  if (!Array.isArray(payload)) return [];
  if (payload.length === 0) return [];

  if (payload.every((item) => item && typeof item === 'object' && !Array.isArray(item))) {
    return payload as SpaceWeatherReport[];
  }

  const rows = payload as unknown[];
  const first = rows[0];
  const hasHeader =
    Array.isArray(first) &&
    first.some((cell) => typeof cell === 'string' && cell.toLowerCase() === 'product_id');

  if (hasHeader) {
    const headers = (first as unknown[]).map((cell) => String(cell));
    return rows
      .slice(1)
      .filter(Array.isArray)
      .map((row) => {
        const item: Record<string, unknown> = {};
        headers.forEach((header, index) => {
          item[header] = (row as unknown[])[index];
        });
        return item as SpaceWeatherReport;
      });
  }

  return rows
    .filter(Array.isArray)
    .map((row) => {
      const values = row as unknown[];
      return {
        product_id: typeof values[0] === 'string' ? values[0] : undefined,
        issue_datetime: typeof values[1] === 'string' ? values[1] : undefined,
        message: typeof values[2] === 'string' ? values[2] : undefined,
      } as SpaceWeatherReport;
    });
};

export function SpaceWeatherWaveformWidget() {
  const [reports, setReports] = useState<SpaceWeatherReport[]>([]);
  const [selectedRingId, setSelectedRingId] = useState(spaceWeatherWaveformRingConfigs[0]?.id ?? 'geomagnetic-activity');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribe = () => {};
    let cancelled = false;

    (async () => {
      const loadFromProxy = async () => {
        const url = buildAppsationsFunctionUrl('proxySpaceWeatherRss');
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`Proxy fetch failed (${response.status})`);
        }
        const payload = await response.json();
        const normalized = normalizeWaveformReports(payload)
          .filter((report) => Boolean(report?.issue_datetime || report?.pubDate || report?.message));
        if (cancelled) return;
        setReports(normalized);
        setError(null);
        setLoading(false);
      };

      try {
        const db = await getDb();
        if (!db) {
          await loadFromProxy();
          return;
        }
        const baseRef = collection(db, 'spaceWeatherReports');
        unsubscribe = onSnapshot(
          query(baseRef, orderBy('issue_datetime', 'desc'), limit(200)),
          (snap) => {
            if (cancelled) return;
            setReports(snap.docs.map((doc) => doc.data() as SpaceWeatherReport));
            setLoading(false);
          },
          async (e) => {
            if (!cancelled) {
              try {
                await loadFromProxy();
              } catch (proxyError: any) {
                console.error('Space weather Firestore error:', e?.message, 'Proxy error:', proxyError?.message);
                setError(null);
                setReports([]);
                setLoading(false);
              }
            }
          }
        );
      } catch (e: any) {
        if (!cancelled) {
          try {
            await loadFromProxy();
          } catch (proxyError: any) {
            console.error('Space weather init error:', e?.message, 'Proxy error:', proxyError?.message);
            setError(null);
            setReports([]);
            setLoading(false);
          }
        }
      }
    })();

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  const selectedRing = spaceWeatherWaveformRingConfigs.find((ring) => ring.id === selectedRingId)
    ?? spaceWeatherWaveformRingConfigs[0];

  const rows = useMemo(() => {
    if (!selectedRing) return [];
    return reports
      .map((report) => {
        const value = selectedRing.parseValue(report);
        if (!value) return null;
        const timestamp = report.issue_datetime ?? report.pubDate ?? '';
        if (!timestamp) return null;
        return {
          timestamp,
          value: value.raw ?? '',
          numericValue: value.numeric,
          stageLabel: selectedRing.stageLabelForValue(value.raw ?? value.numeric),
          productId: report.product_id,
          message: report.message,
        } as RingRow;
      })
      .filter((row): row is RingRow => Boolean(row))
      .slice(0, 80);
  }, [reports, selectedRing]);

  const maxValue = useMemo(() => {
    const numericValues = rows.map((row) => row.numericValue).filter((value): value is number => typeof value === 'number');
    if (numericValues.length === 0) return 0;
    return Math.max(...numericValues.map((value) => Math.abs(value)));
  }, [rows]);

  return (
    <div className="rounded-2xl border border-border/60 bg-card/60 p-4 text-foreground shadow-sm h-full flex flex-col gap-3">
      <div className="flex flex-col gap-1 border-b border-border/40 pb-2">
        <div className="flex items-center gap-2 text-lg font-semibold">
          <span role="img" aria-label="space">🛰️</span>
          Space Weather Waveforms
        </div>
        <p className="text-xs text-muted-foreground">
          Charted Firebase alerts mapped into waveform-ready cycle stages.
        </p>
      </div>

      {loading && <div className="text-sm text-muted-foreground">Loading waveform data…</div>}
      {error && <div className="text-sm text-destructive">{error}</div>}

      {!loading && !error && selectedRing && (
        <Tabs value={selectedRingId} onValueChange={setSelectedRingId} className="flex-1 flex flex-col">
          <TabsList className="flex flex-wrap gap-2 bg-transparent p-0">
            {spaceWeatherWaveformRingConfigs.map((ring) => (
              <TabsTrigger
                key={ring.id}
                value={ring.id}
                className={cn(
                  'flex items-center gap-1 rounded-full border border-border/60 bg-muted/40 px-3 py-1 text-xs text-muted-foreground',
                  selectedRingId === ring.id && 'bg-primary/10 text-primary border-primary/40'
                )}
              >
                {ring.icon}
                {ring.label}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={selectedRingId} className="flex-1 flex flex-col gap-3 pt-3">
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-2 rounded-full border border-border/40 bg-muted/40 px-3 py-1">
                <span className="font-medium text-foreground">{selectedRing.label}</span>
                <span>Waveform: {selectedRing.waveform}</span>
              </div>
              <div className="flex items-center gap-2 rounded-full border border-border/40 bg-muted/40 px-3 py-1">
                {selectedRing.unit ? `Unit: ${selectedRing.unit}` : 'Unitless cycle'}
              </div>
              <div className="text-xs">{selectedRing.description}</div>
            </div>

            <div className="flex-1 min-h-0 overflow-hidden rounded-xl border border-border/50 bg-background/60">
              <div className="overflow-auto">
                <table className="min-w-[560px] w-full text-xs">
                  <thead className="sticky top-0 bg-background/90 backdrop-blur">
                    <tr className="text-left text-muted-foreground">
                      <th className="px-3 py-2 font-semibold">Timestamp</th>
                      <th className="px-3 py-2 font-semibold">Value</th>
                      <th className="px-3 py-2 font-semibold">Stage</th>
                      <th className="px-3 py-2 font-semibold">Source</th>
                      <th className="px-3 py-2 font-semibold">Chart</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr>
                        <td colSpan={5} className="px-3 py-3 text-muted-foreground">
                          No data available for this cycle yet.
                        </td>
                      </tr>
                    )}
                    {rows.map((row) => {
                      const barWidth = maxValue > 0 && row.numericValue !== undefined
                        ? Math.min(100, Math.abs(row.numericValue) / maxValue * 100)
                        : 0;
                      return (
                        <tr key={`${row.timestamp}-${row.value}`} className="border-t border-border/40">
                          <td className="px-3 py-2 font-mono text-[11px] text-muted-foreground whitespace-nowrap">
                            {format(new Date(row.timestamp), 'yyyy-MM-dd HH:mm')}
                          </td>
                          <td className="px-3 py-2 font-semibold text-foreground">
                            {row.value}
                            {selectedRing.unit ? ` ${selectedRing.unit}` : ''}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">
                            {row.stageLabel ?? '—'}
                          </td>
                          <td className="px-3 py-2 text-[11px] text-muted-foreground">
                            <div className="space-y-1">
                              {row.productId ? (
                                <div className="font-mono text-[10px] text-foreground/70">
                                  [{row.productId}]
                                </div>
                              ) : (
                                <div className="text-[10px] text-muted-foreground">Unknown product</div>
                              )}
                              {row.message ? (
                                <details>
                                  <summary className="cursor-pointer text-[11px] text-primary/80">
                                    View alert text
                                  </summary>
                                  <pre className="mt-1 max-w-[360px] whitespace-pre-wrap rounded bg-muted/40 p-2 text-[10px] text-muted-foreground">
                                    {row.message}
                                  </pre>
                                </details>
                              ) : (
                                <div className="text-[10px] text-muted-foreground">No message</div>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2">
                            <div className="h-2 w-full rounded-full bg-muted/50">
                              <div
                                className="h-2 rounded-full bg-primary/60"
                                style={{ width: `${barWidth}%` }}
                              />
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
